/**
 * BotSlashMenu — TG-style slash-команды для DM с ботом зарплаты.
 *
 * UX: юзер в инпуте печатает «/» → всплывает popup со списком команд.
 *     Стрелки ↑↓ навигируют, Enter/Tab/клик — отправляет команду немедленно.
 *     ESC закрывает popup без отправки.
 *
 * Компонент не управляет state'ом — родитель (ChatPage) передаёт:
 *   - query: текущий текст инпута если начинается с «/», иначе null
 *   - selectedIdx + setSelectedIdx — индекс подсвеченной команды
 *   - onPick(cmd) — отправить команду
 *   - onClose — закрыть popup
 */
import { useEffect, useMemo, useRef } from "react";

export interface SlashCommand {
  cmd: string;
  desc: string;
}

/** Все команды бота. is_admin фильтрует «/cancel» — он бессмыслен для менеджера. */
export const BOT_SLASH_COMMANDS: SlashCommand[] = [
  { cmd: "/start",  desc: "главное меню" },
  { cmd: "/menu",   desc: "главное меню" },
  { cmd: "/help",   desc: "помощь" },
  { cmd: "/cancel", desc: "отмена / сброс" },
];

export function filterCommands(query: string | null): SlashCommand[] {
  if (query == null) return [];
  const q = query.toLowerCase().trim();
  if (!q.startsWith("/")) return [];
  if (q === "/") return BOT_SLASH_COMMANDS;
  return BOT_SLASH_COMMANDS.filter(c => c.cmd.toLowerCase().startsWith(q));
}

export function BotSlashMenu({
  query,
  selectedIdx,
  onPick,
  onClose,
}: {
  query: string | null;
  selectedIdx: number;
  onPick: (cmd: string) => void;
  onClose: () => void;
}) {
  const items = useMemo(() => filterCommands(query), [query]);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Прокрутка выделенного в видимую область (как TG)
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el && (el as HTMLElement).scrollIntoView) {
      (el as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [selectedIdx]);

  // Закрытие по клику вне popup. Не закрываем при клике внутри popup
  // (там onMouseDown.preventDefault уже стоит) и при клике на textarea
  // композера (иначе ввод «/» сразу же закрывал бы popup).
  useEffect(() => {
    if (query == null) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (listRef.current && listRef.current.contains(t)) return;
      if (t.closest(".chat-form-input, .chat-form-bot-menu")) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [query, onClose]);

  if (query == null || items.length === 0) return null;

  return (
    <div className="bot-slash-menu" ref={listRef} onMouseDown={(e) => e.preventDefault()}>
      {items.map((c, i) => (
        <button
          key={c.cmd}
          type="button"
          data-idx={i}
          className={`bot-slash-item${i === selectedIdx ? " is-active" : ""}`}
          onClick={() => { onPick(c.cmd); onClose(); }}
        >
          <span className="bot-slash-cmd">{c.cmd}</span>
          <span className="bot-slash-desc">— {c.desc}</span>
        </button>
      ))}
    </div>
  );
}
